import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { recoverRequestsApi, type VersionUrl } from '../api'
import { Copy, Check, RefreshCw, ChevronDown, ChevronRight, FileKey, Download, FolderDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'

interface VersionUrlsTableProps {
    requestID: number | string
}

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    const kb = bytes / 1024
    if (kb < 1024) return `${kb.toFixed(1)} KB`
    const mb = kb / 1024
    if (mb < 1024) return `${mb.toFixed(1)} MB`
    return `${(mb / 1024).toFixed(2)} GB`
}

function formatExpiry(iso: string | null): { text: string; isExpired: boolean } {
    if (!iso) return { text: '—', isExpired: false }
    const d = new Date(iso)
    if (isNaN(d.getTime())) return { text: '—', isExpired: false }
    const now = new Date()
    const isExpired = d < now
    const dateStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    const timeStr = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
    return { text: `${dateStr} ${timeStr}`, isExpired }
}

function downloadFile(url: string, fileName: string, dirName: string) {
    // Use direct navigation — S3 pre-signed URLs don't support CORS fetch
    const a = document.createElement('a')
    a.href = url
    a.download = `${dirName}_${fileName}`
    a.target = '_blank'
    a.rel = 'noopener noreferrer'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
}

export function VersionUrlsTable({ requestID }: VersionUrlsTableProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const [copiedId, setCopiedId] = useState<number | null>(null)
    const [expanded, setExpanded] = useState(false)
    const [isRefreshing, setIsRefreshing] = useState(false)

    const { data: urlsResponse } = useQuery({
        queryKey: ['version-urls', requestID],
        queryFn: async () => {
            const res = await recoverRequestsApi.getVersionUrls(requestID)
            const payload = res.data?.data ?? res.data
            // Handle both old format (array) and new format ({ files, objectName })
            if (Array.isArray(payload)) return { files: payload, objectName: '' }
            return { files: payload?.files ?? [], objectName: payload?.objectName ?? '' }
        },
        enabled: !!requestID,
    })

    const urls: VersionUrl[] = urlsResponse?.files ?? []
    const objectName = urlsResponse?.objectName ?? ''

    const handleCopy = async (url: string, id: number) => {
        try {
            await navigator.clipboard.writeText(url)
            setCopiedId(id)
            toast({ title: 'Copied', description: 'URL copied to clipboard' })
            setTimeout(() => setCopiedId(null), 2000)
        } catch {
            toast({ title: 'Error', description: 'Failed to copy', variant: 'destructive' })
        }
    }

    const handleRefresh = async (e: React.MouseEvent) => {
        e.stopPropagation()
        setIsRefreshing(true)
        try {
            await recoverRequestsApi.requestPreSignedUrls(requestID)
        } catch {}
        await queryClient.refetchQueries({ queryKey: ['version-urls', requestID] })
        setIsRefreshing(false)
        toast({ title: 'Refreshed', description: 'Pre-signed URLs updated' })
    }

    const handleDownloadAll = (e: React.MouseEvent) => {
        e.stopPropagation()
        const validUrls = urls.filter(u => u.preSignedUrl && u.fileName)
        if (validUrls.length === 0) return

        const dirName = objectName || 'recovery-files'

        // Trigger downloads with a small delay between each to avoid browser blocking
        validUrls.forEach((url, i) => {
            setTimeout(() => {
                downloadFile(url.preSignedUrl!, url.fileName!, dirName)
            }, i * 500)
        })

        toast({
            title: 'Downloading',
            description: `${validUrls.length} file${validUrls.length !== 1 ? 's' : ''} — ${dirName}/`,
        })
    }

    return (
        <div className="rounded-lg border bg-card shadow-sm">
            {/* Header — always visible */}
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-2 w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors rounded-lg"
            >
                {expanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                )}
                <FileKey className="h-4 w-4 text-blue-500 flex-shrink-0" />
                <span className="text-sm font-semibold">{t('recoverRequests.preSignedUrls')}</span>
                {urls.length > 0 && (
                    <Badge variant="secondary" className="text-xs px-1.5 py-0">
                        {urls.length}
                    </Badge>
                )}
                <div className="ml-auto flex items-center gap-1 flex-shrink-0">
                    {urls.length > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={handleDownloadAll}
                            title="Download all files"
                        >
                            <FolderDown className="h-3.5 w-3.5" />
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        title={t('recoverRequests.refreshPreSignedUrls')}
                    >
                        <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
            </button>

            {/* Collapsible table */}
            {expanded && urls.length > 0 && (
                <div className="border-t max-h-[280px] overflow-y-auto">
                    <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm">
                            <tr className="border-b">
                                <th className="text-left font-medium px-4 py-2">{t('recoverRequests.fileName')}</th>
                                <th className="text-right font-medium px-3 py-2 w-20 whitespace-nowrap">{t('recoverRequests.fileSize', 'Size')}</th>
                                <th className="text-center font-medium px-2 py-2 w-10"></th>
                                <th className="text-center font-medium px-2 py-2 w-10"></th>
                                <th className="text-left font-medium px-3 py-2 w-36 whitespace-nowrap">{t('recoverRequests.expiry')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {urls.map((url: VersionUrl) => {
                                const expiry = formatExpiry(url.expiryDateTime)
                                return (
                                    <tr key={url.id} className="border-b last:border-b-0 hover:bg-muted/30 transition-colors">
                                        <td className="px-4 py-2.5">
                                            <span className="font-medium text-foreground" title={url.fileName ?? ''}>
                                                {url.fileName || '—'}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-muted-foreground whitespace-nowrap">
                                            {url.fileSize != null ? formatFileSize(url.fileSize) : '—'}
                                        </td>
                                        <td className="px-2 py-2.5 text-center">
                                            {url.preSignedUrl && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 w-7 p-0"
                                                    onClick={() => handleCopy(url.preSignedUrl!, url.id)}
                                                    title="Copy URL"
                                                >
                                                    {copiedId === url.id ? (
                                                        <Check className="h-3.5 w-3.5 text-emerald-500" />
                                                    ) : (
                                                        <Copy className="h-3.5 w-3.5" />
                                                    )}
                                                </Button>
                                            )}
                                        </td>
                                        <td className="px-2 py-2.5 text-center">
                                            {url.preSignedUrl && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 w-7 p-0"
                                                    onClick={() => {
                                                        const dirName = objectName || 'recovery-files'
                                                        downloadFile(url.preSignedUrl!, url.fileName!, dirName)
                                                    }}
                                                    title="Download file"
                                                >
                                                    <Download className="h-3.5 w-3.5" />
                                                </Button>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {expiry.isExpired ? (
                                                <Badge variant="destructive" className="text-xs font-normal px-1.5 py-0">
                                                    {expiry.text}
                                                </Badge>
                                            ) : (
                                                <span className="text-muted-foreground whitespace-nowrap">{expiry.text}</span>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {expanded && urls.length === 0 && (
                <div className="border-t px-4 py-3">
                    <p className="text-xs text-muted-foreground">{t('recoverRequests.noPreSignedUrls')}</p>
                </div>
            )}
        </div>
    )
}
